import { useNavigate, useLocation } from 'react-router-dom';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import {
  faEnvelope,
  faGear,
  faClipboardList,
  faBullhorn,
  faClock,
} from '@fortawesome/free-solid-svg-icons';
import { PLATFORM_ROUTES } from '@/routes/platform-routes';
import { cn } from '@/lib/cn';
import { EmailTemplateLibrary } from './EmailTemplateLibrary';
import { PlatformEmailGatewayPage } from './PlatformEmailGatewayPage';

const TABS = [
  { label: 'Email Templates', path: PLATFORM_ROUTES.COMMUNICATION_TEMPLATES, icon: faEnvelope },
  { label: 'Email Gateways', path: PLATFORM_ROUTES.COMMUNICATION_GATEWAYS, icon: faGear },
  { label: 'Email Logs', path: PLATFORM_ROUTES.COMMUNICATION_LOGS, icon: faClipboardList },
];

function PlaceholderSection({ title, description }: { title: string; description: string }) {
  return (
    <div className="flex flex-col items-center justify-center rounded-xl border border-border bg-card py-20 text-center">
      <div className="flex h-14 w-14 items-center justify-center rounded-full bg-muted">
        <FontAwesomeIcon icon={faBullhorn} className="h-6 w-6 text-muted-foreground" />
      </div>
      <h2 className="mt-4 text-base font-semibold text-foreground">{title}</h2>
      <p className="mt-2 max-w-sm text-sm text-muted-foreground">{description}</p>
      <div className="mt-6 flex items-center gap-2 rounded-full border border-border px-4 py-2 text-xs text-muted-foreground">
        <FontAwesomeIcon icon={faClock} className="h-3.5 w-3.5" />
        Available in Phase 12.3
      </div>
    </div>
  );
}

export function CommunicationHubPage() {
  const navigate = useNavigate();
  const { pathname } = useLocation();

  const activeTab = TABS.find((t) => pathname === t.path) ?? TABS[0];

  return (
    <div className="space-y-5">
      <div>
        <h1 className="text-xl font-semibold text-foreground">Communication</h1>
        <p className="mt-0.5 text-sm text-muted-foreground">
          Platform-wide communication assets — email templates, gateways, and logs
        </p>
      </div>

      {/* Tabs */}
      <div className="flex gap-1 rounded-xl border border-border bg-muted/30 p-1">
        {TABS.map((tab) => (
          <button
            key={tab.path}
            onClick={() => navigate(tab.path)}
            className={cn(
              'flex flex-1 items-center justify-center gap-2 rounded-lg px-4 py-2 text-sm font-medium transition-all duration-150',
              activeTab.path === tab.path
                ? 'bg-card text-foreground shadow-sm'
                : 'text-muted-foreground hover:text-foreground',
            )}
          >
            <FontAwesomeIcon icon={tab.icon} className="h-3.5 w-3.5" />
            <span className="hidden sm:block">{tab.label}</span>
          </button>
        ))}
      </div>

      {/* Content */}
      {activeTab.path === PLATFORM_ROUTES.COMMUNICATION_TEMPLATES && <EmailTemplateLibrary />}
      {activeTab.path === PLATFORM_ROUTES.COMMUNICATION_GATEWAYS && <PlatformEmailGatewayPage />}
      {activeTab.path === PLATFORM_ROUTES.COMMUNICATION_LOGS && (
        <PlaceholderSection
          title="Email Logs"
          description="Cross-tenant email delivery logs, bounce tracking, and delivery status monitoring. Requires email audit infrastructure."
        />
      )}
    </div>
  );
}
